/**
 * *"Escolha seu personagem"* — uma vez, à primeira entrada.
 *
 * Existe porque a pergunta só vivia no formulário de registo, e com o portão do
 * painel ligado ninguém passa por lá: a equipa inteira entrava e ficava com a
 * mesma personagem sem nunca ter escolhido nada. Quem decide se isto aparece é o
 * `characterPickedAt` do próprio — `null` é "nunca foi perguntado". O `gender`
 * não servia: tem valor por omissão, portanto toda a gente tem sempre um.
 *
 * **Não se diz uma palavra sobre género.** Nem no texto, nem nos rótulos, nem no
 * `aria-label`, nem no `alt` das imagens — foi um pedido explícito, e um leitor
 * de ecrã a anunciar "masculino/feminino" quebrava-o tão bem como um rótulo à
 * vista. Cada opção é só a personagem, desenhada de frente, e chama-se
 * "Personagem 1" e "Personagem 2". A coluna na base continua a chamar-se
 * `gender` porque é a que o mundo 2D já lê para desenhar o boneco; isso é
 * vocabulário de esquema, e não chega aos olhos de ninguém.
 *
 * A arte é a MESMA do escritório, não um desenho à parte que dessa se afastaria
 * ao primeiro retoque: a feminina é o PNG de frente tal e qual, e o masculino é
 * o recorte da folha do campus nas coordenadas que o `officeChar.ts` usa para o
 * frame parado — linha `down`, passo parado, corpo de (16,7) a 32×54.
 */

import type { Gender } from "@greenvoice/shared";
import { useState } from "react";

import { api } from "../lib/api";
import { useStore } from "../store/store";

/** Escala inteira: a 3× cada pixel da arte são nove, sem um único meio pixel.
 *  Qualquer fracção aqui borrata o contorno — é pixel art, não uma fotografia. */
const ESCALA = 3;

/** O corpo dentro da folha do campus, como o `officeChar.ts` o recorta. */
const MASCULINO = { x: 16, y: 7, w: 32, h: 54 } as const;
/** A feminina já vem em ficheiro próprio, no tamanho do frame. */
const FEMININA = { w: 32, h: 48 } as const;

export function CharacterPicker() {
  const self = useStore((s) => s.self);
  const [aGravar, setAGravar] = useState<Gender | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Já escolheu (ou ainda não há sessão): o ecrã não existe.
  if (!self || self.characterPickedAt !== null) return null;

  async function escolher(personagem: Gender) {
    if (aGravar) return;
    setAGravar(personagem);
    setErro(null);
    try {
      const actualizado = await api.users.updateSelf({ gender: personagem });
      useStore.setState((s) => ({
        self: actualizado,
        users: { ...s.users, [actualizado.id]: { ...s.users[actualizado.id], ...actualizado } },
      }));
    } catch (caught) {
      // Fica no ecrã com o motivo à vista: fechá-lo sem ter gravado dava uma
      // escolha que não sobrevivia ao próximo arranque, e ninguém saberia porquê.
      setErro(caught instanceof Error ? caught.message : "Não foi possível guardar. Tenta outra vez.");
      setAGravar(null);
    }
  }

  return (
    <div className="charpick" role="dialog" aria-modal="true" aria-labelledby="charpick-titulo">
      <div className="charpick__card">
        <h1 className="charpick__titulo" id="charpick-titulo">
          Escolha seu personagem
        </h1>

        <div className="charpick__opcoes">
          <button
            type="button"
            className={`charpick__opcao${aGravar === "MALE" ? " is-gravando" : ""}`}
            onClick={() => void escolher("MALE")}
            disabled={aGravar !== null}
            aria-label="Personagem 1"
          >
            <span
              className="charpick__boneco"
              style={{
                width: MASCULINO.w * ESCALA,
                height: MASCULINO.h * ESCALA,
                backgroundImage: 'url("/campus/personagem-v1.png")',
                backgroundSize: `${256 * ESCALA}px ${256 * ESCALA}px`,
                backgroundPosition: `-${MASCULINO.x * ESCALA}px -${MASCULINO.y * ESCALA}px`,
              }}
            />
          </button>

          <button
            type="button"
            className={`charpick__opcao${aGravar === "FEMALE" ? " is-gravando" : ""}`}
            onClick={() => void escolher("FEMALE")}
            disabled={aGravar !== null}
            aria-label="Personagem 2"
          >
            <span
              className="charpick__boneco"
              style={{
                width: FEMININA.w * ESCALA,
                height: FEMININA.h * ESCALA,
                backgroundImage: 'url("/campus/char-fem-down-v1.png")',
                backgroundSize: `${FEMININA.w * ESCALA}px ${FEMININA.h * ESCALA}px`,
              }}
            />
          </button>
        </div>

        {erro ? (
          <p className="charpick__erro" role="alert">
            {erro}
          </p>
        ) : (
          <p className="charpick__nota">Dá para trocar depois, nas definições.</p>
        )}
      </div>
    </div>
  );
}
